<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <oxd-text tag="h6" class="orangehrm-main-title">Leave Report</oxd-text>
      </div>

      <oxd-form class="orangehrm-leave-report-form" @submit-valid="onGenerate">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.mode"
                type="select"
                label="Mode"
                :options="modeOptions"
                :clear="false"
              />
            </oxd-grid-item>
            <oxd-grid-item v-if="filters.mode?.id === 'single'">
              <oxd-input-field
                v-model="filters.month"
                type="input"
                label="Month (YYYY-MM)"
                placeholder="2026-07"
              />
              <oxd-text class="orangehrm-input-hint" tag="p">
                Payroll period: 26 of previous month → 25 of this month
              </oxd-text>
            </oxd-grid-item>
            <template v-else>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="filters.fromMonth"
                  type="input"
                  label="From month (YYYY-MM)"
                  placeholder="2026-01"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="filters.toMonth"
                  type="input"
                  label="To month (YYYY-MM)"
                  placeholder="2026-06"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="filters.month"
                  type="input"
                  label="View month in table (YYYY-MM)"
                  placeholder="2026-01"
                />
              </oxd-grid-item>
            </template>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-actions>
          <oxd-button
            display-type="secondary"
            label="Generate"
            type="submit"
            :disabled="loading"
          />
          <oxd-button
            class="orangehrm-left-space"
            display-type="ghost"
            label="Download Excel"
            :disabled="loading"
            @click="onDownload"
          />
        </oxd-form-actions>
      </oxd-form>

      <div v-if="error" class="orangehrm-leave-report-error">
        <oxd-text tag="p">{{ error }}</oxd-text>
      </div>

      <div v-if="report" class="orangehrm-leave-report-meta">
        <oxd-text tag="p">
          {{ report.meta.label }} ({{ report.meta.from }} → {{ report.meta.to }}) —
          {{ report.meta.employeeCount }} employees
        </oxd-text>
        <oxd-text tag="p" class="orangehrm-input-hint">
          Base = entitlement at period start · +1 CL each month · minus
          applied/approved leave · Permission ignored · ABS only when AMS
          data exists for the month
        </oxd-text>
      </div>

      <div v-if="report" class="orangehrm-leave-report-scroll">
        <table class="orangehrm-leave-report-table">
          <thead>
            <tr>
              <th>Sl.</th>
              <th>Code</th>
              <th>Name</th>
              <th v-for="(d, i) in report.dayHeaders" :key="'h' + i">{{ d }}</th>
              <th>Total</th>
              <th>CL</th>
              <th>PL</th>
              <th>RH</th>
              <th>CLB</th>
              <th>PLB</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="emp in report.employees" :key="emp.empNumber">
              <td>{{ emp.sl }}</td>
              <td>{{ emp.employeeCode }}</td>
              <td class="--name">{{ emp.name }}</td>
              <td
                v-for="(cell, i) in emp.days"
                :key="emp.empNumber + '-' + i"
                :class="{ '--red': cell.color === 'red' }"
              >
                {{ cell.text }}
              </td>
              <td>{{ emp.totalLeaves }}</td>
              <td>{{ emp.usage.CL }}</td>
              <td>{{ emp.usage.PL }}</td>
              <td>{{ emp.usage.RH }}</td>
              <td>{{ emp.closingBalance.CL }}</td>
              <td>{{ emp.closingBalance.PL }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {navigate} from '@/core/util/helper/navigation';

const MODE_SINGLE = {id: 'single', label: 'Single month'};
const MODE_RANGE = {id: 'range', label: 'Custom months (multi-sheet Excel)'};

export default {
  name: 'AdminLeaveReport',
  props: {
    defaultMonth: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      error: '',
      report: null,
      modeOptions: [MODE_SINGLE, MODE_RANGE],
      filters: {
        mode: MODE_SINGLE,
        month: this.defaultMonth || '',
        fromMonth: '',
        toMonth: '',
      },
      http: new APIService(
        window.appGlobal.baseUrl,
        '/api/v2/leave/admin-leave-report',
      ),
    };
  },
  methods: {
    async onGenerate() {
      this.loading = true;
      this.error = '';
      try {
        const params = {};
        if (this.filters.mode?.id === 'range') {
          params.fromMonth = this.filters.fromMonth;
          params.toMonth = this.filters.toMonth;
          params.month = this.filters.month || this.filters.fromMonth;
        } else {
          params.month = this.filters.month;
        }
        const {data} = await this.http.getAll(params);
        const rows = data?.data || [];
        const first = rows[0];
        if (first?.report) {
          this.report = first.report;
        } else {
          this.report = first || null;
        }
        if (!this.report) {
          this.error = 'No data returned';
        }
      } catch (e) {
        let msg = 'Failed to load leave report';
        try {
          const data = e && e.response ? e.response.data : null;
          if (data && data.error && data.error.message) {
            msg = data.error.message;
          } else if (e && e.message) {
            msg = e.message;
          }
        } catch (ignore) {
          // ignore
        }
        this.error = String(msg);
        this.report = null;
      } finally {
        this.loading = false;
      }
    },
    onDownload() {
      const q = {};
      if (this.filters.mode?.id === 'range') {
        q.fromMonth = this.filters.fromMonth;
        q.toMonth = this.filters.toMonth;
      } else {
        q.month = this.filters.month;
      }
      navigate('/leave/exportAdminLeaveReport', {}, q);
    },
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-leave-report-form {
  padding: 0 1.2rem 1rem;
}
.orangehrm-leave-report-meta {
  padding: 0 1.2rem 0.5rem;
}
.orangehrm-leave-report-error {
  padding: 0 1.2rem;
  color: #c62828;
}
.orangehrm-input-hint {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}
.orangehrm-left-space {
  margin-left: 0.5rem;
}
.orangehrm-leave-report-scroll {
  overflow: auto;
  max-height: 70vh;
  padding: 0 1rem 1.2rem;
}
.orangehrm-leave-report-table {
  border-collapse: collapse;
  font-size: 12px;
  min-width: 100%;
  th,
  td {
    border: 1px solid #e2e8f0;
    padding: 4px 6px;
    white-space: nowrap;
    text-align: center;
  }
  th {
    background: #f1f5f9;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  td.--name {
    text-align: left;
    min-width: 140px;
  }
  td.--red {
    color: #c62828;
    font-weight: 600;
  }
}
</style>
